import { css } from '@emotion/react';
import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { formatCentsToCurrency } from '@frontend/currency';
import { ManualTemplateTypesWithStaticTemplate, SendInThreadModalContents } from '@frontend/integrated-messaging';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { formatDate, Text } from '@frontend/design-system';
import { useValidPhoneNumberFromInvoice } from '../../../../hooks/use-valid-phone-number-from-invoice';
import { useValidPhoneNumberFromPerson } from '../../../../hooks/use-valid-phone-number-from-person';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { CollectModalStateCareCreditMessageType } from '../../../collect-payment-multi.types';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { CareCreditMessageBodyProps } from './care-credit-message.types';

const styles = {
  loader: css`
    height: 200px;
  `,
  body: css`
    .wv-modal-header {
      display: none;
    }

    .wv-modal-footer {
      padding-top: ${theme.spacing(2)};
    }

    .step__body {
      /** Add flex to body, so that the message grow:1 works */
      display: flex;
      flex-direction: column;

      /** Compensate for CareCreditMessageBody having it's own padding 8px 24px */
      padding-left: 0;
      padding-right: 0;
      margin-top: ${theme.spacing(2)};
    }

    .wv-modal-flex-content {
      @media screen and (min-width: ${breakpoints.mobile.max}px) {
        min-height: 580px;
      }
    }
  `,
};

export const MESSAGE_TYPE_MAP: Record<CollectModalStateCareCreditMessageType, ManualTemplateTypesWithStaticTemplate> = {
  'payment-link': TemplateType_Slug.MANUAL_CARE_CREDIT_COLLECT,
  'pre-approved-link': TemplateType_Slug.MANUAL_CARE_CREDIT_PREAPPROVED,
  'enrollment-link': TemplateType_Slug.MANUAL_CARE_CREDIT_APPLICATION,
};

export const CareCreditMessageBody = ({ onGoBack, onCompleteStep }: CareCreditMessageBodyProps) => {
  const { invoice, activeLocationId, person, careCreditMessageType, careCreditData } = useCollectPaymentMultiContext();

  const { firstValidPhoneNumber: firstInvoicePhoneNumber } = useValidPhoneNumberFromInvoice(invoice);

  /**
   * Thanks to the CareCredit payments panel, we might have a flow with no invoice.
   * So we needto have a person fallback, too. Internally, the CareCredit multi-steppers only do the valid SMS lookup
   * via person's id, rather than invoice.person.id, etc.
   */
  const { firstValidPhoneNumber: firstPersonPhoneNumber } = useValidPhoneNumberFromPerson(activeLocationId, person);

  const onSend = () => {
    setTimeout(() => onCompleteStep(), 2000);
  };

  const templateType = MESSAGE_TYPE_MAP[careCreditMessageType || 'payment-link'];

  const initPersonPhone = firstInvoicePhoneNumber || firstPersonPhoneNumber;

  return (
    <StyledStep
      id={PAYMENT_MULTI_STEPS.careCreditMessage}
      label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.careCreditMessage]}
      hasInternalFooter
      components={{
        Footer: () => null,
      }}
      css={styles.body}
    >
      <SendInThreadModalContents
        groupId={invoice?.locationId || activeLocationId || ''}
        onBack={onGoBack}
        personId={invoice?.person?.id || person?.personId || ''}
        initPersonPhone={initPersonPhone}
        templateType={templateType}
        linkData={{
          link: careCreditData?.patientLink ?? '',
          relatedId: invoice?.id || person?.personId || '',
        }}
        onSchedule={onCompleteStep}
        onSend={onSend}
        onClose={onCompleteStep}
        contextPreviewProps={
          invoice && {
            iconName: 'dollar-sign',
            title: <Text weight='bold'>{formatCentsToCurrency(invoice?.billedAmount)}</Text>,
            subtitle: <Text>Due Date: {formatDate(invoice?.billedAt, 'MMM D, YYYY')}</Text>,
          }
        }
      />
    </StyledStep>
  );
};
